import React, { useState } from "react";
import { Reveal, Overline } from "./Reveal";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Facebook, Instagram, Send } from "lucide-react";
import { LOGO } from "@/data/content";
import { useContent } from "@/context/ContentContext";

export function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const content = useContent();

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await api.post("/newsletter", { email });
      toast.success("You're on the list! Welcome to The Epic Center family.");
      setEmail("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer id="contact" data-testid="footer" className="bg-epic-black border-t border-white/5 pt-20 pb-10">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-3 gap-12 lg:gap-16">
          {/* Brand + contact */}
          <div>
            <img src={LOGO} alt="The Epic Center Birmingham logo" className="h-auto w-full max-w-[240px] object-contain" />
            <div className="mt-6 space-y-3 text-white/70 text-sm">
              <p className="flex items-start gap-3"><MapPin size={18} className="text-epic-gold mt-0.5 shrink-0" /> {content.contact_address1}<br />{content.contact_address2}</p>
              <p className="flex items-center gap-3"><Phone size={18} className="text-epic-gold shrink-0" /> {content.contact_phone1}</p>
              <p className="flex items-center gap-3"><Phone size={18} className="text-epic-gold shrink-0" /> {content.contact_phone2}</p>
              <p className="flex items-center gap-3"><Mail size={18} className="text-epic-gold shrink-0" /> {content.contact_email}</p>
            </div>
            <div className="flex gap-3 mt-6">
              {[{ Icon: Facebook, name: "facebook" }, { Icon: Instagram, name: "instagram" }].map(({ Icon, name }, i) => (
                <a key={name} href="#" data-testid={`social-${i}`} className="p-3 border border-white/15 text-white/80 hover:bg-epic-gold hover:text-epic-black hover:border-epic-gold transition-colors">
                  <Icon size={18} />
                </a>
              ))}
              <a href="#" data-testid="social-tiktok" className="p-3 border border-white/15 text-white/80 hover:bg-epic-gold hover:text-epic-black hover:border-epic-gold transition-colors text-sm font-bold w-11 flex items-center justify-center">
                TT
              </a>
            </div>
          </div>

          {/* Newsletter */}
          <div>
            <Overline>Stay In The Loop</Overline>
            <h3 className="font-display text-3xl text-white mt-3">Join The Newsletter</h3>
            <p className="text-white/60 text-sm mt-3">Be first to know about events, showcases, and special offers.</p>
            <form onSubmit={subscribe} className="mt-5 flex">
              <input
                data-testid="newsletter-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email address"
                className="flex-1 bg-transparent border border-white/15 text-white placeholder-white/30 px-4 py-3 text-sm focus:outline-none focus:border-epic-gold"
              />
              <button
                data-testid="newsletter-submit"
                disabled={loading}
                className="bg-epic-gold text-epic-black px-5 hover:bg-epic-goldhover transition-colors disabled:opacity-60"
              >
                <Send size={18} />
              </button>
            </form>
          </div>

          {/* Map */}
          <div>
            <Overline>Find Us</Overline>
            <div className="mt-4 overflow-hidden border border-white/10">
              <iframe
                title="The Epic Center Birmingham map"
                data-testid="google-map"
                src="https://www.google.com/maps?q=1301+3rd+Avenue+West+Birmingham+AL+35208&output=embed"
                className="w-full h-[240px] grayscale contrast-125"
                loading="lazy"
              />
            </div>
          </div>
        </div>

        <div className="border-t border-white/5 mt-14 pt-6 flex flex-col md:flex-row justify-between items-center gap-3 text-white/40 text-xs">
          <p>© {new Date().getFullYear()} The Epic Center Birmingham. All rights reserved.</p>
          <a href="/admin" data-testid="admin-link" className="hover:text-epic-gold transition-colors">Admin Portal</a>
        </div>
      </div>
    </footer>
  );
}
